/* THE KEYBOARD CANON (Tier-1 A5, Ale rulings 2026-09-04) — one model for
 * every list, drawer and card:
 *
 *   J / K   move the cursor            Enter  opens the object
 *   Esc     closes what you opened and returns focus to the row
 *   ?       the shortcuts sheet
 *
 * ONE hook, used by every queue and every deck stream, so no surface grows
 * its own listener with its own holes. The guards live here once:
 *   · a key typed into a field (input, textarea, select, contenteditable)
 *     belongs to the field — the reject-reason box never pages the stream;
 *   · a handled event (`defaultPrevented`, or an inner Esc that stopped
 *     propagation) is left alone — innermost first;
 *   · Enter on a focused control (button, link, row) is that control's own
 *     Enter — the list never fires a second action behind it.
 */

import { useEffect } from 'react'
import { fundMenuOpen } from '../fundScope'

/** The key press belongs to a field, not to the page. */
export function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  if (t.isContentEditable) return true
  const tag = t.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/** Focus sits on something with its own Enter (a button, a link, a
 *  focusable row) — the list's Enter must not double it. */
export function isInteractiveTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  return (
    t.closest(
      'button, a[href], input, select, textarea, summary, [role="button"], [tabindex]:not([tabindex="-1"])'
    ) !== null
  )
}

export interface ListKeys {
  /** J */
  onNext?: () => void
  /** K */
  onPrev?: () => void
  /** Enter — only when focus is not on a control of its own. */
  onEnter?: () => void
  /** Esc — only when no inner handler took it first. */
  onEscape?: () => void
  enabled?: boolean
}

export function useListKeys({ onNext, onPrev, onEnter, onEscape, enabled = true }: ListKeys) {
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      if (isTypingTarget(e.target)) return
      // The fund selector owns the keyboard while its menu stands
      // (Tier-2 D1, Ale 2026-09-05) — one guard for every queue.
      if (fundMenuOpen()) return
      switch (e.key) {
        case 'j':
          if (onNext) {
            e.preventDefault()
            onNext()
          }
          break
        case 'k':
          if (onPrev) {
            e.preventDefault()
            onPrev()
          }
          break
        case 'Enter':
          if (onEnter && !isInteractiveTarget(e.target)) {
            e.preventDefault()
            onEnter()
          }
          break
        case 'Escape':
          if (onEscape) onEscape()
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onNext, onPrev, onEnter, onEscape, enabled])
}

/** Move focus by `delta` through the focusable rows matching `selector`
 *  inside `root` — the cursor of a table whose rows are the focus ring
 *  itself (the fund Record). When no row is focused the walk counts from
 *  the `anchor` row (the open object's row) if there is one, else it
 *  lands on the first row. */
export function focusRow(
  root: ParentNode | null,
  selector: string,
  delta: number,
  anchor?: string
) {
  if (!root) return
  const rows = Array.from(root.querySelectorAll<HTMLElement>(selector))
  if (rows.length === 0) return
  let at = rows.findIndex((r) => r === document.activeElement || r.contains(document.activeElement))
  if (at < 0 && anchor) at = rows.findIndex((r) => r.matches(anchor))
  const next = rows[Math.min(rows.length - 1, Math.max(0, (at < 0 ? -1 : at) + delta))]
  next?.focus()
  next?.scrollIntoView({ block: 'nearest' })
}
